import { useEffect, useLayoutEffect } from 'react'
import { SIGN_IN_PATH } from '../api'
import { BottomBar } from '../components/BottomBar'
import { ToastHost } from '../components/Toast'
import { FilmSession } from '../screens/FilmSession'
import { IdeaDetail } from '../screens/IdeaDetail'
import { LinkedIn } from '../screens/LinkedIn'
import { Pipeline } from '../screens/Pipeline'
import { openQuickAdd, QuickAdd } from '../screens/QuickAdd'
import { Search } from '../screens/Search'
import { stageOf } from '../domain/stages'
import { loadIdeas, useIdeas } from '../state/ideas'
import { loadQueue, useQueue } from '../state/queue'
import { Link } from './Link'
import { matchRoute, navigate, restoreScroll, usePath, type Route } from './routing'
import { useKeyboardInset, useWide } from './viewport'

export function App() {
  const path = usePath()
  const route = matchRoute(path)
  const { phase } = useIdeas()

  useKeyboardInset()
  useTypingFlag()
  useShortcuts()

  useEffect(() => {
    void loadIdeas()
    void loadQueue()
    // An installed PWA is resumed, not reloaded, so a phone that was in a
    // pocket all afternoon would otherwise show the morning's list. Ideas
    // added from Claude sessions in the meantime appear on return.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void loadIdeas()
      void loadQueue()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  useLayoutEffect(() => {
    restoreScroll()
  }, [path])

  if (phase === 'signed-out') return <SignedOut />

  return (
    <>
      <div className="status-scrim" aria-hidden="true" />
      <Screen route={route} />
      {route.name !== 'film-session' && <BottomBar current={tabFor(route)} />}
      <QuickAdd />
      <ToastHost />
    </>
  )
}

function Screen({ route }: { route: Route }) {
  switch (route.name) {
    case 'pipeline':
      return <Pipeline />
    case 'idea':
      return <IdeaScreen id={route.id} />
    case 'linkedin':
      return <LinkedIn />
    case 'film-session':
      return <FilmSession key={route.batchId} batchId={route.batchId} />
    case 'search':
      return <Search />
    case 'not-found':
      return (
        <main className="screen">
          <p className="empty">
            Nothing lives here. <Link to="/">Back to the pipeline</Link>
          </p>
        </main>
      )
  }
}

/**
 * On a laptop an idea in the pipeline opens in a panel beside the canvas, so
 * its card stays in view. On the phone, and for an idea that is not on the
 * canvas (a LinkedIn topic, a published or killed idea found in Search), it is
 * a page of its own, and Back returns to where it was opened from.
 */
function IdeaScreen({ id }: { id: number }) {
  const wide = useWide()
  const { ideas } = useIdeas()
  const { ids } = useQueue()
  const idea = ideas.find((item) => item.id === id)
  const onCanvas = !idea || stageOf(idea, ids) !== null
  return wide && onCanvas ? <Pipeline openId={id} /> : <IdeaDetail key={id} id={id} />
}

function tabFor(route: Route): '/' | '/linkedin' | '/search' | null {
  if (route.name === 'pipeline' || route.name === 'idea') return '/'
  if (route.name === 'linkedin') return '/linkedin'
  if (route.name === 'search') return '/search'
  return null
}

function SignedOut() {
  const next = encodeURIComponent(window.location.pathname)
  return (
    <main className="signed-out">
      <h1 className="pipeline-title">Content</h1>
      <p>Every idea, from the first thought to the published reel.</p>
      <a className="button button-primary" href={`${SIGN_IN_PATH}?next=${next}`}>
        Sign in with Google
      </a>
    </main>
  )
}

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))

/**
 * `data-typing` on the root while a field has focus. The bottom bar steps
 * aside then: on Android the keyboard resizes the page, and a bar pinned to
 * the bottom would sit on the keys, over the field being typed into.
 */
function useTypingFlag() {
  useEffect(() => {
    const root = document.documentElement
    const onFocusIn = (event: FocusEvent) => root.toggleAttribute('data-typing', isTypingTarget(event.target))
    const onFocusOut = () => root.removeAttribute('data-typing')
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])
}

/** On a laptop: `n` for a new idea, `/` to search, `g` then `p` or `l` for the pipeline or LinkedIn. */
function useShortcuts() {
  useEffect(() => {
    let pendingG = false
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return
      if (document.querySelector('.sheet-layer[data-open="true"]')) return
      if (event.key === 'n' || event.key === 'c') {
        event.preventDefault()
        const onLinkedIn = matchRoute(window.location.pathname).name === 'linkedin'
        openQuickAdd('idea', onLinkedIn ? 'linkedin' : undefined)
      } else if (event.key === '/') {
        event.preventDefault()
        navigate('/search')
      } else if (pendingG && event.key === 'l') navigate('/linkedin')
      else if (pendingG && event.key === 'p') navigate('/')
      pendingG = event.key === 'g'
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}
